import { useEffect, useState, type ReactElement } from "react";
import { View } from "@-/com.npmjs.react-native";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { captureSentryException } from "@-/dev.improve.haniverse.lib.sentry";
import {
  SlowNetworkNotice,
  useSlowStaticNetwork,
} from "@-/dev.improve.haniverse.components.ui.slow-network-notice";

/**
 * How long a boundary must stay suspended before it counts as STUCK.
 *
 * Suspending is normal: a navigation commits before its destination's data exists, and this
 * fallback is the page's loading state. What is worth a notice and a report is a boundary that
 * keeps the skeleton up — a normal suspension resolves in well under a second, so anything past
 * this is the pathological case.
 */
const STUCK_SUSPENSION_REPORT_DELAY_MS = 8000;

export function SuspensePageFallback({
  children,
  operation,
}: {
  children: ReactElement;
  /** Names what the boundary is loading (`page load <module>`), for the stuck report. */
  operation: string;
}) {
  const { isDegraded: isNetworkDegraded } = useSlowStaticNetwork();
  // Past the stuck threshold the skeleton says so: a visible "still loading" state until the
  // boundary's deadline (`SUSPENSE_DEADLINE_MS`) turns it into the error screen.
  const [isStuck, setIsStuck] = useState(false);

  // Report a PERSISTENT suspension to Sentry so a stuck boundary is visible in prod: a suspended
  // page renders as a skeleton with no error, so without this a user stuck for 30s and a page that
  // loaded in 40ms look identical from the outside.
  //
  // Only a boundary still suspended after `STUCK_SUSPENSION_REPORT_DELAY_MS` reports. The cleanup
  // cancels the timer, so a boundary that resolves (unmounting the fallback) never reports at all.
  // Reporting on mount fired for every suspension — including the instant, healthy ones — and
  // turned normal arrivals into ~658 Sentry events across 282 users (HAN-2).
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setIsStuck(true);
      captureSentryException(new Error("SuspensePageBoundary stuck suspended"), {
        extra: { operation },
        level: "warning",
        tags: {
          boundary: "suspense-page",
          phase: "suspended",
        },
      });
    }, STUCK_SUSPENSION_REPORT_DELAY_MS);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [operation]);

  return (
    <View className="min-h-0 flex-1">
      {children}
      {isNetworkDegraded ? (
        <View className="absolute inset-x-0 bottom-0 bg-background">
          <SlowNetworkNotice />
        </View>
      ) : isStuck ? (
        <View
          accessibilityLiveRegion="polite"
          className="absolute inset-x-0 bottom-0 items-center bg-background p-6"
          testID="suspense-page-still-loading"
        >
          <Text className="text-center text-[13px] text-muted-foreground">
            This is taking longer than usual…
          </Text>
        </View>
      ) : null}
    </View>
  );
}
